import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, ChevronRight, LogIn } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { serviceStructure } from '../data/services';

const LOGO_ICON_URL = "/logo-icon.png";
const LOGO_TEXT_URL = "/logo-text.png";
const LOGO_ICON_WEBP = "/logo-icon.webp";
const LOGO_TEXT_WEBP = "/logo-text.webp";
const PHONE = "587-355-FLOW (3569)";

// Build navigation from service structure
const buildServiceNav = () => {
  return Object.values(serviceStructure).map(service => ({
    name: service.name,
    path: `/${service.slug}`,
    children: service.subServices.map(sub => ({
      name: sub.name,
      path: `/${service.slug}/${sub.slug}`
    }))
  }));
};

const serviceNavItems = buildServiceNav();

const navItems = [
  { name: 'Plumbing', path: '/plumbing', hasDropdown: true },
  { name: 'Heating', path: '/heating', hasDropdown: true },
  { name: 'Air Conditioning', path: '/air-conditioning', hasDropdown: true },
  { name: 'Gas Fitting', path: '/gas-fitting', hasDropdown: true },
  { name: 'Expert Tips', path: '/blog' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

// Membership Top Bar Component
export const MembershipBar = () => {
  return (
    <div className="bg-gradient-to-r from-[#1E3A5F] to-[#152A45] text-white py-2.5 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Desktop Layout - Horizontal */}
        <div className="hidden sm:flex items-center justify-center gap-4 text-center">
          <span className="text-sm font-medium">
            Save on Repairs <span className="text-[#3B82F6] font-bold">•</span> Priority Service <span className="text-[#3B82F6] font-bold">•</span> Extended Warranty Protection
          </span>
          <Link
            to="/membership"
            className="bg-white text-[#1E3A5F] font-bold text-xs px-4 py-1.5 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            View Membership Plans
          </Link>
        </div>
        
        {/* Mobile Layout - Stacked */}
        <div className="flex sm:hidden flex-col items-center justify-center gap-2 text-center">
          <span className="text-xs font-medium">
            Save on Repairs <span className="text-[#3B82F6] font-bold">•</span> Priority Service <span className="text-[#3B82F6] font-bold">•</span> Extended Warranty
          </span>
          <Link
            to="/membership"
            className="bg-white text-[#1E3A5F] font-bold text-xs px-4 py-1.5 rounded-full hover:bg-gray-100 transition-colors"
          >
            View Membership Plans
          </Link>
        </div>
      </div>
    </div>
  );
};

// Service Dropdown Component
const ServiceDropdown = ({ service, isOpen, onClose }) => {
  const serviceData = serviceStructure[service];
  if (!serviceData) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.15 }}
          className="absolute top-full left-0 mt-2 w-72 bg-white shadow-2xl rounded-xl py-3 border border-gray-100 z-50"
          onMouseLeave={onClose}
        >
          <div className="px-4 pb-2 mb-2 border-b border-gray-100">
            <Link 
              to={`/${service}`}
              className="text-[#1E3A5F] font-bold text-sm hover:text-[#3B82F6] transition-colors"
            >
              All {serviceData.name} Services →
            </Link>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {serviceData.subServices.map((sub) => (
              <Link
                key={sub.slug}
                to={`/${service}/${sub.slug}`}
                className="block px-4 py-2.5 text-sm text-gray-600 hover:text-[#1E3A5F] hover:bg-gray-50 transition-colors"
              >
                {sub.name}
              </Link>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [expandedMobileService, setExpandedMobileService] = useState(null);
  const location = useLocation();
  const dropdownTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    setExpandedMobileService(null);
  }, [location]);

  const handleMouseEnter = (service) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setOpenDropdown(service);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  const getServiceSlug = (name) => {
    return name.toLowerCase().replace(' ', '-');
  };

  return (
    <>
      <header
        data-testid="main-header"
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
          isScrolled ? 'shadow-lg' : ''
        }`}
      >
        {/* Row 1: Logo */}
        <div className="border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              {/* Logo - Icon + Large Text */}
              <Link to="/" data-testid="logo-link" className="flex items-center gap-3 sm:gap-4">
                <picture>
                  <source srcSet={LOGO_ICON_WEBP} type="image/webp" />
                  <img 
                    src={LOGO_ICON_URL} 
                    alt="" 
                    width="139"
                    height="160"
                    className="h-14 sm:h-16 md:h-20 w-auto object-contain flex-shrink-0"
                  />
                </picture>
                <picture>
                  <source srcSet={LOGO_TEXT_WEBP} type="image/webp" />
                  <img 
                    src={LOGO_TEXT_URL} 
                    alt="Flow Forge Mechanical" 
                    width="308"
                    height="96"
                    className="h-12 sm:h-14 md:h-16 lg:h-20 w-auto object-contain"
                  />
                </picture>
              </Link>

              {/* Desktop Phone */}
              <div className="hidden lg:flex items-center gap-4">
                <a
                  href={`tel:+15873553569`}
                  data-testid="header-phone"
                  className="flex items-center gap-2 text-[#1E3A5F] hover:text-[#3B82F6] transition-colors font-bold text-lg"
                >
                  <Phone className="w-5 h-5" />
                  <span>{PHONE}</span>
                </a>
              </div>

              {/* Mobile Menu Button */}
              <button
                data-testid="mobile-menu-btn"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-gray-700 hover:text-[#1E3A5F] transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
              </button>
            </div>
          </div>
        </div>

        {/* Row 2: Navigation (Desktop only) */}
        <div className="hidden lg:block bg-gray-50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-12">
              {/* Desktop Navigation */}
              <nav className="flex items-center space-x-1">
                {navItems.map((item) => (
                  <div 
                    key={item.name} 
                    className="relative"
                    onMouseEnter={() => item.hasDropdown && handleMouseEnter(getServiceSlug(item.name))}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                      className={`flex items-center gap-1 px-4 py-3 font-semibold text-sm tracking-wide uppercase transition-colors ${
                        location.pathname.startsWith(item.path) 
                          ? 'text-[#1E3A5F]' 
                          : 'text-gray-600 hover:text-[#1E3A5F]'
                      }`}
                    >
                      {item.name}
                      {item.hasDropdown && (
                        <ChevronDown className={`w-4 h-4 transition-transform ${
                          openDropdown === getServiceSlug(item.name) ? 'rotate-180' : ''
                        }`} />
                      )}
                    </Link>
                    
                    {item.hasDropdown && (
                      <ServiceDropdown 
                        service={getServiceSlug(item.name)}
                        isOpen={openDropdown === getServiceSlug(item.name)}
                        onClose={() => setOpenDropdown(null)}
                      />
                    )}
                  </div>
                ))}
              </nav>

              {/* Log In + Request Service */}
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  data-testid="header-login-link"
                  className="flex items-center gap-1.5 text-[#1E3A5F] font-semibold text-sm uppercase tracking-wide hover:text-[#3B82F6] transition-colors"
                >
                  <LogIn className="w-4 h-4" />
                  Log In
                </Link>
                <Link
                  to="/contact"
                  data-testid="header-book-btn"
                  className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold uppercase tracking-wider text-sm px-6 py-2.5 rounded-lg transition-colors"
                >
                  Request Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-x-0 top-[120px] z-40 lg:hidden bg-white shadow-xl border-b border-gray-200 overflow-hidden"
          >
            <nav className="flex flex-col p-4 max-h-[calc(100vh-140px)] overflow-y-auto">
              {navItems.map((item) => (
                <div key={item.name} className="border-b border-gray-100 last:border-0">
                  {item.hasDropdown ? (
                    <>
                      <button
                        onClick={() => setExpandedMobileService(
                          expandedMobileService === getServiceSlug(item.name) 
                            ? null 
                            : getServiceSlug(item.name)
                        )}
                        className="w-full flex items-center justify-between text-base font-heading font-bold text-gray-800 py-3 hover:text-[#1E3A5F]"
                      >
                        <span>{item.name}</span>
                        <ChevronRight className={`w-5 h-5 transition-transform ${
                          expandedMobileService === getServiceSlug(item.name) ? 'rotate-90' : ''
                        }`} />
                      </button>
                      <AnimatePresence>
                        {expandedMobileService === getServiceSlug(item.name) && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="ml-4 pb-3 space-y-1 overflow-hidden"
                          >
                            <Link
                              to={item.path}
                              className="block text-sm text-[#1E3A5F] font-semibold py-2"
                            >
                              All {item.name} Services →
                            </Link>
                            {serviceStructure[getServiceSlug(item.name)]?.subServices.map((sub) => (
                              <Link
                                key={sub.slug}
                                to={`/${getServiceSlug(item.name)}/${sub.slug}`}
                                className="block text-sm text-gray-600 hover:text-[#1E3A5F] py-1.5"
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className="block text-base font-heading font-bold text-gray-800 py-3 hover:text-[#1E3A5F]"
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Mobile CTA Section */}
              <div className="pt-4 mt-2 border-t border-gray-200 space-y-3">
                <a
                  href={`tel:+15873553569`}
                  className="flex items-center justify-center gap-2 text-[#1E3A5F] text-lg font-bold py-2"
                >
                  <Phone className="w-5 h-5" />
                  {PHONE}
                </a>
                <Link
                  to="/login"
                  data-testid="mobile-login-link"
                  className="flex items-center justify-center gap-2 border border-[#1E3A5F] text-[#1E3A5F] font-bold uppercase tracking-wider py-3 rounded-lg hover:bg-[#1E3A5F]/5 transition-colors"
                >
                  <LogIn className="w-5 h-5" />
                  Log In
                </Link>
                <Link
                  to="/contact"
                  className="block text-center bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold uppercase tracking-wider py-3 rounded-lg transition-colors"
                >
                  Request Service
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
